import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse, CityLookup, CountryLookup } from '@/api/types'
import type {
  CompanyBank,
  CompanyBankListItem,
  CompanyBanksListResponse,
  Level5Account,
} from '@/api/types'

function unwrapData<T>(payload: any): T {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data as T
    if ('data' in payload) return payload.data as T
  }
  return payload as T
}

function normalizeLookupArray<T extends { id: number }>(payload: any): T[] {
  const raw = unwrapData<any>(payload)
  if (!Array.isArray(raw)) return []
  return raw
}

export class CompanyBanksService {
  async getAll(branchId: number, pageNumber: number, pageSize: number): Promise<CompanyBanksListResponse> {
    const url = endpoints.companyBanks.getAll(branchId, pageNumber, pageSize)
    const res = await apiClient.get<any>(url, { skipHandledLocallyErrorUI: true } as any)
    const payload = res.data

    const data = unwrapData<any>(payload)

    if (data && typeof data === 'object') {
      const list = (data.data ?? data.items ?? data.result ?? data) as any
      const totalRecords = Number(data.totalRecords ?? data.total ?? data.totalCount ?? 0)
      if (Array.isArray(list)) {
        return { data: list as CompanyBankListItem[], totalRecords }
      }
    }

    if (Array.isArray(data)) {
      return { data: data as CompanyBankListItem[], totalRecords: (data as any[]).length }
    }

    return { data: [], totalRecords: 0 }
  }

  async getById(id: number): Promise<CompanyBank> {
    const url = endpoints.companyBanks.getById(id)
    const res = await apiClient.get<ApiResponse<CompanyBank> | CompanyBank | any>(url, {
      skipHandledLocallyErrorUI: true,
    } as any)
    return unwrapData<CompanyBank>(res.data)
  }

  async getLevel5Accounts(branchId: number): Promise<Level5Account[]> {
    const url = endpoints.companyBanks.getLevel5Accounts(branchId)
    const res = await apiClient.get<any>(url, { skipHandledLocallyErrorUI: true } as any)
    return normalizeLookupArray<Level5Account>(res.data)
  }

  async getCountryLookups(): Promise<CountryLookup[]> {
    const res = await apiClient.get<any>(endpoints.companyBanks.getCountryLookups, {
      skipHandledLocallyErrorUI: true,
    } as any)
    return normalizeLookupArray<CountryLookup>(res.data)
  }

  async getCityLookups(countryId: number): Promise<CityLookup[]> {
    const url = endpoints.companyBanks.getCityLookups(countryId)
    const res = await apiClient.get<any>(url, { skipHandledLocallyErrorUI: true } as any)
    return normalizeLookupArray<CityLookup>(res.data)
  }

  async getStateLookups(cityId: number): Promise<CityLookup[]> {
    const url = endpoints.companyBanks.getStateLookups(cityId)
    const res = await apiClient.get<any>(url, { skipHandledLocallyErrorUI: true } as any)
    return normalizeLookupArray<CityLookup>(res.data)
  }

  async save(payload: CompanyBank): Promise<any> {
    const res = await apiClient.post(endpoints.companyBanks.save, payload, { skipHandledLocallyErrorUI: true } as any)
    return res.data
  }

  async update(payload: CompanyBank): Promise<any> {
    const res = await apiClient.put(endpoints.companyBanks.update, payload, { skipHandledLocallyErrorUI: true } as any)
    return res.data
  }

  async delete(id: number): Promise<any> {
    const url = endpoints.companyBanks.delete(id)
    const res = await apiClient.delete(url, { skipHandledLocallyErrorUI: true } as any)
    return res.data
  }
}

export const companyBanksService = new CompanyBanksService()

